import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { enableSound, disableSound, isSoundEnabled, blip, NOTES } from "@/lib/sound";
import { ArrowRight, Sparkles, Eye as EyeIcon, Volume2, VolumeX, Send } from "lucide-react";

const A = (n) => `/assets/es/${n}.webp`;

/* ---------------- Cursor trail (canvas, electric sparkles) ---------------- */
function CursorTrail() {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const onResize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);
    const colors = ["#FF2E9A", "#21E6D3", "#FFE14D", "#FFFFFF"];
    let particles = [];
    let last = { x: -100, y: -100 };
    const onMove = (e) => {
      last = { x: e.clientX, y: e.clientY };
      const dots = 2;
      for (let i = 0; i < dots; i++) {
        particles.push({
          x: e.clientX + (Math.random() - 0.5) * 8,
          y: e.clientY + (Math.random() - 0.5) * 8,
          vx: (Math.random() - 0.5) * 1.2,
          vy: (Math.random() - 0.5) * 1.2 + 0.4,
          life: 1,
          size: Math.random() * 2.4 + 1,
          color: colors[(Math.random() * colors.length) | 0],
        });
      }
      if (particles.length > 220) particles = particles.slice(-220);
    };
    window.addEventListener("mousemove", onMove);
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy; p.life -= 0.028;
        if (p.life <= 0) continue;
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 10; ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      particles = particles.filter((p) => p.life > 0);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={canvasRef} className="es-trail" style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 10 }} data-testid="cursor-trail" />;
}

/* ---------------- Floating decorative artwork ---------------- */
function Decor({ src, className, style, dur = 7, float = true }) {
  return (
    <img
      src={A(src)} alt="" aria-hidden="true"
      className={`pointer-events-none absolute select-none ${float ? "es-float" : ""} ${className || ""}`}
      style={{ animationDuration: `${dur}s`, ...style }}
    />
  );
}

/* ---------------- Reveal wrapper ---------------- */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------------- Flip card (symbolic dictionary) ---------------- */
function FlipCard({ img, name, meaning, testid, tears, note, onHover }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`es-flip group h-[420px] cursor-pointer ${flipped ? "is-flipped" : ""}`}
      onClick={() => setFlipped((f) => !f)}
      onMouseEnter={() => onHover && onHover(note)}
      data-testid={testid}
    >
      <div className="es-flip-inner relative w-full h-full">
        {/* front */}
        <div className="es-flip-face absolute inset-0 rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent flex flex-col items-center justify-center px-6 overflow-hidden">
          <div className="relative">
            <img src={img} alt={name} className="max-h-52 w-auto object-contain drop-shadow-[0_0_28px_rgba(33,230,211,0.35)]" />
            {tears && (
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                {[0, 0.4, 0.8].map((d, i) => (
                  <img key={i} src={A("teardrop")} alt="" className="es-drip-tear w-3" style={{ animationDelay: `${d}s` }} />
                ))}
              </div>
            )}
          </div>
          <h3 className="es-gothic text-3xl mt-8 es-glow-turq">{name}</h3>
          <span className="mt-3 text-[11px] uppercase tracking-[0.35em] text-white/40">tap to reveal</span>
        </div>
        {/* back */}
        <div className="es-flip-face es-flip-back absolute inset-0 rounded-3xl border border-[var(--es-pink)]/40 bg-[#141014] flex flex-col items-center justify-center px-8 text-center">
          <h3 className="es-gothic text-2xl es-glow-pink mb-5">{name}</h3>
          <p className="es-body text-white/75 text-base leading-relaxed">{meaning}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Collection card ---------------- */
function CollectionCard({ img, kicker, title, desc, etsy, testid, onHover }) {
  return (
    <Reveal className="group relative rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[var(--es-turq)]/40 transition-colors">
      <div onMouseEnter={() => onHover && onHover()} className="aspect-square flex items-center justify-center p-10 bg-[radial-gradient(circle_at_50%_40%,rgba(33,230,211,0.08),transparent_60%)]">
        <img src={img} alt={title} className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105 group-hover:rotate-1" />
      </div>
      <div className="p-7 border-t border-white/10">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--es-turq)] mb-2">{kicker}</p>
        <h3 className="es-gothic text-2xl mb-3">{title}</h3>
        <p className="es-body text-white/60 text-sm leading-relaxed mb-5">{desc}</p>
        <a href={etsy} target="_blank" rel="noreferrer" data-testid={testid}
           className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[var(--es-pink)] transition-colors">
          Shop on Etsy <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </Reveal>
  );
}

/* ---------------- Newsletter ---------------- */
function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setStatus("sending");
    try {
      const r = await api.post("/public/newsletter", { email });
      setStatus("done");
      toast.success(r.data.already ? "You're already on the list ✦" : "You're in. Welcome to the signal ✦");
      setEmail("");
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
  };
  return (
    <section id="signal-list" data-testid="es-newsletter" className="relative py-24 overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
        <Reveal>
          <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">Stay in the Signal</p>
          <h2 className="es-gothic text-4xl lg:text-5xl es-glow-pink mb-5">Be First to Feel<br />the Next Collection</h2>
          <p className="es-body text-white/60 max-w-xl mx-auto mb-9">
            No noise, no spam — just a quiet signal when new hand-drawn artifacts drop. Join the outsiders and
            the dreamers.
          </p>
          {status === "done" ? (
            <p data-testid="newsletter-success" className="es-gothic text-2xl es-glow-turq">✦ Signal received. Welcome. ✦</p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                data-testid="newsletter-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                className="flex-1 bg-white/[0.05] border border-white/15 rounded-full px-6 py-4 text-white placeholder-white/35 outline-none focus:border-[var(--es-turq)] transition-colors"
              />
              <button data-testid="newsletter-submit" disabled={status === "sending"}
                      className="es-btn-pink px-7 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {status === "sending" ? "Sending…" : <>Join <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export default function EmotionalSignalLanding() {
  const [etsy, setEtsy] = useState("https://www.etsy.com");
  const [settings, setSettings] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [soundOn, setSoundOn] = useState(false);

  const toggleSound = async () => {
    if (isSoundEnabled()) {
      disableSound();
      setSoundOn(false);
    } else {
      await enableSound();
      setSoundOn(true);
      blip(NOTES.high);
    }
  };
  const hoverBlip = (freq) => blip(freq || NOTES.soft);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const eyeX = useTransform(sx, (v) => v * 26);
  const eyeY = useTransform(sy, (v) => v * 26);
  const layerBX = useTransform(sx, (v) => v * 55);
  const layerBY = useTransform(sy, (v) => v * 55);

  useEffect(() => {
    document.title = "EmotionalSignal — Surreal Art for Unspoken Feelings";
    api.get("/public/settings").then((r) => {
      setSettings(r.data);
      if (r.data.social_etsy) setEtsy(r.data.social_etsy);
      // safe tracking injection (structured IDs only)
      const s = r.data;
      if (s.gtm_id && /^GTM-[A-Z0-9]+$/i.test(s.gtm_id) && !document.getElementById("gtm-script")) {
        const el = document.createElement("script"); el.id = "gtm-script";
        el.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.gtm_id}');`;
        document.head.appendChild(el);
      }
      if (s.meta_pixel_id && /^\d{5,}$/.test(s.meta_pixel_id) && !document.getElementById("fb-pixel")) {
        const el = document.createElement("script"); el.id = "fb-pixel";
        el.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.meta_pixel_id}');fbq('track','PageView');`;
        document.head.appendChild(el);
      }
    }).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <div className="es-root es-noise es-body min-h-screen relative" onMouseMove={onMouseMove} data-testid="es-landing">
      <CursorTrail />

      {/* Sound toggle */}
      <button onClick={toggleSound} data-testid="sound-toggle" aria-label="Toggle sound"
        className={`fixed z-50 bottom-6 left-6 w-12 h-12 rounded-full flex items-center justify-center border transition-all ${soundOn ? "bg-[var(--es-turq)]/15 border-[var(--es-turq)] text-[var(--es-turq)] shadow-[0_0_24px_rgba(33,230,211,0.5)]" : "bg-white/5 border-white/20 text-white/60 hover:text-white"}`}>
        {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        {soundOn && <span className="absolute inset-0 rounded-full border border-[var(--es-turq)]/50 animate-ping pointer-events-none" />}
      </button>


      {/* Header */}
      <header data-testid="es-header"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,padding] duration-300 ${scrolled ? "bg-[#0B0B0B]/80 backdrop-blur-xl border-b border-white/10 py-4" : "py-6"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <a href="#top" className="es-gothic text-2xl es-glow-turq">EmotionalSignal</a>
          <nav className="hidden md:flex items-center gap-9 text-sm text-white/70">
            <a href="#manifesto" className="hover:text-[var(--es-turq)] transition-colors">Manifesto</a>
            <a href="#symbols" className="hover:text-[var(--es-turq)] transition-colors">Symbols</a>
            <a href="#collections" className="hover:text-[var(--es-turq)] transition-colors">Artifacts</a>
            <a href={etsy} target="_blank" rel="noreferrer" data-testid="header-etsy"
               className="es-btn-pink px-5 py-2 rounded-full font-semibold">Shop on Etsy</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section id="top" data-testid="es-hero" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
        <Decor src="planet-pink" className="w-40 md:w-56 top-24 right-[6%] opacity-80" dur={9} />
        <Decor src="planet-turq" className="w-24 md:w-32 bottom-24 left-[8%] opacity-70" dur={11} />
        <Decor src="stars-gold" className="w-20 top-40 left-[14%] es-twinkle" float={false} />
        <Decor src="sparkle-turq" className="w-12 bottom-40 right-[24%] es-twinkle" float={false} />
        <Decor src="bolt" className="w-10 top-1/3 right-[16%]" dur={6} />
        <Decor src="star-heart" className="w-14 bottom-28 right-[42%]" dur={8} />

        <div className="relative z-20 max-w-7xl mx-auto w-full px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-6">
              Surreal Art · Handmade Signals
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="es-gothic text-5xl sm:text-6xl lg:text-7xl es-glow-pink">
              Surreal Art for<br />Unspoken Feelings
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.9 }}
              className="es-body text-lg text-white/70 mt-7 max-w-xl">
              A home for beautifully complicated minds and dreamers. A bridge made of neon and nervous
              energy, designed to span the void between isolation and connection.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 mt-10">
              <a href={etsy} target="_blank" rel="noreferrer" data-testid="hero-primary-cta"
                 className="es-btn-pink px-8 py-4 rounded-full font-bold inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Step Into the Signal
              </a>
              <a href="#symbols" data-testid="hero-secondary-cta"
                 className="es-btn-ghost px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2">
                <EyeIcon className="w-5 h-5" /> Claim Your Symbol
              </a>
            </motion.div>
          </div>

          <div className="relative flex items-center justify-center min-h-[360px]">
            <motion.div style={{ x: layerBX, y: layerBY }} className="absolute inset-0 flex items-center justify-center">
              <div className="w-[340px] h-[340px] md:w-[440px] md:h-[440px] rounded-full bg-[radial-gradient(circle,rgba(255,46,154,0.28),transparent_62%)] blur-xl" />
            </motion.div>
            <motion.img
              style={{ x: eyeX, y: eyeY }}
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              src={A("eye-hero")} alt="Surreal crying eye with a broken heart pupil, in electric turquoise and pink, representing awareness and vulnerability"
              className="relative z-10 w-[300px] md:w-[420px] drop-shadow-[0_0_50px_rgba(33,230,211,0.4)]"
              data-testid="hero-eye"
            />
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-xs uppercase tracking-[0.3em] animate-pulse">
          Peer through the static ↓
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" data-testid="es-manifesto" className="relative py-28 lg:py-36 overflow-hidden">
        <Decor src="cloud" className="w-56 top-10 right-[4%] opacity-30" dur={13} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={A("faces")} alt="Two surreal blue faces gazing across a deep red horizon — inner conflict and connection in one frame"
                 className="rounded-2xl border border-white/10 w-full" />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-5">The Story Behind the Signals</p>
            <h2 className="es-gothic text-4xl lg:text-5xl es-glow-turq mb-6">The Beauty of<br />Imperfect Intention</h2>
            <hr className="es-hr mb-7" />
            <p className="es-body text-white/70 mb-5">
              In an era of hollow, AI-generated “perfection,” we choose the scar, the wobble, and the weight
              of the human hand. Every piece celebrates its handmade character — hand-drawn artifacts,
              digitized to preserve their grit.
            </p>
            <p className="es-body text-white/70 mb-8">
              By layering bright, playful palettes over darker, visceral imagery, the strange becomes familiar.
              Inner conflict and love are forced to coexist in a single frame — proving there is an unusual
              beauty in the shadows we carry.
            </p>
            <blockquote className="es-gothic text-2xl leading-snug text-white es-glow-pink border-l-2 border-[var(--es-pink)] pl-5">
              “I enjoy combining bright, playful colors with darker emotional imagery — expressive without
              becoming polished or predictable.”
              <span className="block es-body text-sm tracking-wide text-white/50 mt-4">— Rimona Gal</span>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* SYMBOLIC DICTIONARY */}
      <section id="symbols" data-testid="es-symbols" className="relative py-28 lg:py-36 bg-[#080808] overflow-hidden">
        <Decor src="sparkle-gold" className="w-12 top-16 left-[10%] es-twinkle" float={false} />
        <Decor src="hearts-fall" className="w-24 bottom-16 right-[8%] opacity-70" dur={10} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal className="text-center mb-16">
            <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">A Lexicon for Outsiders</p>
            <h2 className="es-gothic text-4xl lg:text-6xl es-glow-turq">The Symbolic Dictionary</h2>
            <p className="es-body text-white/55 mt-5 max-w-xl mx-auto">
              Feelings encoded into a visual language. Tap a symbol to read its meaning.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            <FlipCard testid="symbol-eye" img={A("sym-eye")} name="The Eye" tears note={NOTES.eye} onHover={hoverBlip}
              meaning="The intersection of awareness and intuition. The quiet, heavy power of being seen. A witness to the unseen." />
            <FlipCard testid="symbol-heart" img={A("sym-heart")} name="Heart & Tear" note={NOTES.heart} onHover={hoverBlip}
              meaning="Vulnerability rendered in ink. The unusual beauty of sadness. Inner conflict made visible — sacred and raw." />
            <FlipCard testid="symbol-nazar" img={A("sym-nazar")} name="The Nazar" note={NOTES.nazar} onHover={hoverBlip}
              meaning="Ancient protection. A blue-hued gaze that deflects the mundane. A mystical shield for the dreamer." />
          </div>
          <Reveal className="text-center mt-12">
            <p className="es-body text-white/50 italic">These symbols are ready to be carried out of the dream and into your reality.</p>
          </Reveal>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section id="collections" data-testid="es-collections" className="relative py-28 lg:py-36 overflow-hidden">
        <Decor src="prism" className="w-72 top-6 left-[2%] opacity-40" dur={14} />
        <Decor src="planet-turq" className="w-20 bottom-10 right-[6%] opacity-60" dur={12} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal className="mb-16 max-w-2xl">
            <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">Artifacts of the Inner World</p>
            <h2 className="es-gothic text-4xl lg:text-6xl es-glow-pink mb-5">Live With the Signal</h2>
            <p className="es-body text-white/60">
              Personality anchors — alternative, tattoo-inspired pieces that transform ordinary objects into
              expressions of the soul. Sold on Etsy.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            <CollectionCard testid="collection-tshirt" img={A("eye-drip")} kicker="Alternative Streetwear"
              title="The Wearable Signal" etsy={etsy} onHover={() => hoverBlip(NOTES.eye)}
              desc="Wear your inner world on the outside. Oversized, unisex tees where Gothic aesthetic meets contemporary surrealism." />
            <CollectionCard testid="collection-sticker" img={A("col-sticker")} kicker="Portable Talismans"
              title="Stickers & Charms" etsy={etsy} onHover={() => hoverBlip(NOTES.heart)}
              desc="Waterproof, die-cut, saturated with color. All-Seeing Eye Nazar protection for your laptop or sketchbook." />
            <CollectionCard testid="collection-poster" img={A("col-poster")} kicker="Framed Art Prints"
              title="The Sacred Space" etsy={etsy} onHover={() => hoverBlip(NOTES.nazar)}
              desc="Turn your walls into windows. Original digital prints in sleek matte wood frames — gateways into strange landscapes." />
          </div>
          <Reveal className="text-center mt-14">
            <a href={etsy} target="_blank" rel="noreferrer" data-testid="collections-cta"
               className="es-btn-pink inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold">
              Take ownership of the signal <ArrowRight className="w-5 h-5" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterSection />

      {/* JOIN / FOOTER */}
      <section id="join" data-testid="es-footer" className="relative pt-28 pb-16 bg-[#080808] border-t border-white/10 overflow-hidden">
        <Decor src="heart-sun" className="w-28 top-10 right-[10%] opacity-50" dur={9} />
        <Decor src="crystalball" className="w-24 top-16 left-[8%] opacity-40" dur={12} />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <Reveal>
            <h2 className="es-gothic text-4xl lg:text-6xl es-glow-turq mb-6">Joining the World of<br />EmotionalSignal</h2>
            <p className="es-body text-white/65 max-w-2xl mx-auto mb-10">
              To the outsiders and the dreamers: thank you for being here. This is not a transaction — it is a
              connection. By choosing EmotionalSignal, you support independent, hand-drawn art and find a home
              for the feelings you haven’t quite found the words for yet.
            </p>
            <a href={etsy} target="_blank" rel="noreferrer" data-testid="footer-etsy-cta"
               className="es-btn-pink inline-flex items-center gap-2 px-9 py-4 rounded-full font-bold text-lg">
              Find Your Signal on Etsy <ArrowRight className="w-5 h-5" />
            </a>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-2 mt-12 text-sm text-white/45">
              <span>◈ Based in Israel</span>
              <span>◈ Original Hand-Drawn Digital Artwork</span>
              <span>◈ Accepts Etsy Gift Cards</span>
            </div>
          </Reveal>
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">
          <p className="es-gothic text-xl es-glow-pink">Created with intention & emotion</p>
          <p className="es-body text-white/40 text-sm">© {new Date().getFullYear()} EmotionalSignal · Rimona Gal</p>
        </div>
      </section>
    </div>
  );
}
